import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellValueChangedEvent,
  FirstDataRenderedEvent,
  GetRowIdFunc,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ModelUpdatedEvent,
  RowSelectedEvent,
} from '@ag-grid-community/core';
import { SelectionChangedEvent } from '@ag-grid-community/core/dist/esm/es6/events';
import { AsyncPipe, NgClass } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  input,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';

import { LocalStorageKey } from '@shared/constants/localStorageKey';
import { DEFAULT_PAGINATION_SIZE, setPaginationSize } from '@shared/constants/pagination.constants';
import { TableConstants } from '@shared/constants/table.constants';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import { DatasourceService } from '@shared/services/datasource.service';
import { ServerSideDatasource, ServerSideFilterInfo, ServerSideSortOrder } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { PaginationPanelComponent } from '../pagination-panel/pagination-panel.component';
import { TableSkeletonComponent } from '../table-skeleton/table-skeleton.component';

@Component({
  imports: [
    PaginationPanelComponent,
    TableSkeletonComponent,
    AsyncPipe,
    AgGridAngular,
    NgClass,
    StickyGridDirective,
  ],
  selector: 'aux-pagination-grid',
  templateUrl: './pagination-grid.component.html',
})
export class PaginationGridComponent<T> implements OnInit {
  private datasourceService = inject(DatasourceService);
  private readonly destroyRef = inject(DestroyRef);

  loading = input<boolean>();

  @Output() cellValueChanged = new EventEmitter<CellValueChangedEvent>();
  @Input() classList: string = '';
  @Input({ required: true }) dataSource!: ServerSideDatasource<T>;
  readonly defaultPaginationSize = DEFAULT_PAGINATION_SIZE;
  // eslint-disable-next-line
  @Input() detailCellRenderer: any = undefined;
  @Input() embedFullWidthRows = false;
  @Input() enableFillHandle = false;
  @Input() exportGridOptions?: GridOptions;
  @Input() filterForm?: FormGroup;
  @Input() filterValues$?: BehaviorSubject<Record<string, unknown>>;
  @Output() firstDataRendered = new EventEmitter<FirstDataRenderedEvent>();
  @Input() getContext?: () => unknown;
  @Input() getRowId?: GetRowIdFunc;
  protected gridAPI?: GridApi;
  @Output() gridReady = new EventEmitter<GridReadyEvent>();
  @Input() hidePinnedRow = false;
  @Input({ required: true }) idTable!: string;
  @Input() isAgThemeAlpine = false;
  @Input() isAgThemeFlat = false;
  // eslint-disable-next-line
  @Input() isRowMaster: ((items: any) => boolean) | undefined = undefined;
  @Input() masterDetail = false;
  @Output() modelUpdated = new EventEmitter<ModelUpdatedEvent>();
  @Input() offColumnsToFit = false;
  @Output() paginationChange = new EventEmitter<void>();
  @Input() paginationKey?: LocalStorageKey;
  @Output() rowSelected = new EventEmitter<RowSelectedEvent>();
  @Output() selectionChanged = new EventEmitter<SelectionChangedEvent>();
  @Input() serverSideFilters?: ServerSideFilterInfo<T>[];
  @Output() sortChanged = new EventEmitter<void>();
  @Input() sortModel$?: BehaviorSubject<ServerSideSortOrder<T>[]>;

  private _gridOptions!: GridOptions;
  private readonly defaultGridOptions: GridOptions = {
    ...TableConstants.DEFAULT_GRID_OPTIONS.GRID_OPTIONS,
    defaultColDef: {
      ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
      cellClass: TableConstants.STYLE_CLASSES.CELL_ALIGN_CENTER,
      resizable: true,
    },
    maxConcurrentDatasourceRequests: 1,
    pagination: true,
    paginationPageSize: DEFAULT_PAGINATION_SIZE,
    rowHeight: 45,
    rowModelType: 'serverSide',
    suppressPaginationPanel: true,
  };

  ngOnInit() {
    this.dataSource.refresh$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshGridData());

    this.dataSource.aggregation$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.hidePinnedRow) {
        this.refreshPinnedBottomRow();
      }
    });

    if (this.filterForm) {
      if (!this.serverSideFilters) throw new Error('serverSideFilters input is required');

      if (!this.filterValues$) throw new Error('filterValues$ input is required');

      const formWatcherInfo = this.datasourceService.applyTransformations(
        this.filterForm,
        this.serverSideFilters
      );

      this.filterValues$.next(formWatcherInfo.currentValue);

      formWatcherInfo.valueChangesObservable
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((v) => {
          this.filterValues$?.next(v);
        });
    }
  }

  getGridContext() {
    return this?.getContext ? this.getContext() : null;
  }

  @Input() set gridOptions(value: GridOptions) {
    this._gridOptions = {
      ...this.defaultGridOptions,
      ...value,
    };
  }

  get gridOptions() {
    return this._gridOptions;
  }

  gridSizeChanged() {
    if (!this.offColumnsToFit) {
      this.sizeColumnsToFit();
    }
  }

  onCellValueChanged(event: CellValueChangedEvent): void {
    this.cellValueChanged.emit(event);
  }

  onFirstDataRendered(event: FirstDataRenderedEvent): void {
    if (!this.offColumnsToFit) {
      this.sizeColumnsToFit();
    }

    this.firstDataRendered.emit(event);
  }

  onGridReady(event: GridReadyEvent) {
    this.gridAPI = event.api;
    this.onSortChanged();
    this.gridReady.emit(event);
  }

  onModelUpdated(event: ModelUpdatedEvent): void {
    this.modelUpdated.emit(event);
  }

  onPaginationChange(): void {
    this.paginationChange.emit();

    if (this.paginationKey) {
      setPaginationSize(
        this.paginationKey,
        this.gridAPI?.paginationGetPageSize() || DEFAULT_PAGINATION_SIZE
      );
    }
  }

  onRowSelected(event: RowSelectedEvent): void {
    this.rowSelected.emit(event);
  }

  onSelectionChanged(event: SelectionChangedEvent): void {
    this.selectionChanged.emit(event);
  }

  onSortChanged() {
    const currentSortModel = this.datasourceService.getCurrentSortModel(this.gridAPI);

    this.sortModel$?.next(currentSortModel);
    this.sortChanged.emit();
  }

  private refreshGridData() {
    this.gridAPI?.setGridOption('serverSideDatasource', this.dataSource);
  }

  private refreshPinnedBottomRow() {
    const pinnedBottomRowData: Array<unknown> = [];

    if (this.dataSource.currentServerInput) {
      if (this.dataSource.totalRows) {
        pinnedBottomRowData.push(this.dataSource.aggregation);
      }
    }

    this.gridAPI?.setGridOption('pinnedBottomRowData', pinnedBottomRowData);
  }

  sizeColumnsToFit(): void {
    this.gridAPI?.sizeColumnsToFit();
  }

  updateGridLayout(): void {
    if (!this.gridAPI) return;

    Utils.updateGridLayout(this.gridAPI, this.idTable, true);
  }
}
